import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TopBar } from "@/components/Nav";
import { Confetti, ProgressBar, playCorrect, playWrong } from "@/components/Bits";
import { MEMORY_DECKS, shuffle, pick } from "@/lib/quizgen";
import { recordAnswer, completeActivity } from "@/lib/progress";
import { speak } from "@/lib/speech";

const LEVELS = [
  { key: "easy", label: "Easy", pairs: 4, cols: "grid-cols-4", emoji: "🌱", bonus: 2 },
  { key: "medium", label: "Medium", pairs: 6, cols: "grid-cols-4", emoji: "🌿", bonus: 4 },
  { key: "hard", label: "Hard", pairs: 8, cols: "grid-cols-4", emoji: "🌳", bonus: 6 },
];

export default function MemoryGame() {
  const nav = useNavigate();
  const [deck, setDeck] = useState(null);
  const [level, setLevel] = useState(LEVELS[0]);
  const [cards, setCards] = useState([]);
  const [open, setOpen] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [done, setDone] = useState(false);

  const start = (d = deck, l = level) => {
    const chosen = pick(d.cards, l.pairs);
    setCards(shuffle([...chosen, ...chosen].map((c, i) => ({ ...c, id: i }))));
    setOpen([]); setMatched([]); setMoves(0); setDone(false);
    speak(`${d.label} deck, ${l.label}. Find the matching pairs!`);
  };
  useEffect(() => { if (deck) start(deck, level); }, [deck, level]); // eslint-disable-line react-hooks/exhaustive-deps

  const flip = (i) => {
    if (open.length === 2 || open.includes(i) || matched.includes(i)) return;
    const next = [...open, i];
    setOpen(next);
    speak(cards[i].say);
    if (next.length === 2) {
      setMoves(m => m + 1);
      const [a, b] = next;
      if (cards[a].v === cards[b].v) {
        recordAnswer("memory", true); playCorrect();
        setMatched(prev => [...prev, a, b]);
        setTimeout(() => setOpen([]), 500);
      } else { recordAnswer("memory", false); setTimeout(() => setOpen([]), 900); }
    }
  };

  useEffect(() => {
    if (cards.length && matched.length === cards.length && !done) {
      completeActivity("memory", `${deck.key}-${level.key}`, level.bonus); setDone(true); speak("You matched them all!");
    }
  }, [matched, cards, done, deck, level]);

  if (!deck) return (
    <>
      <TopBar title="Memory Game" backTo="/play" />
      <div className="ll-bg min-h-screen pb-28">
        <main className="max-w-4xl mx-auto px-4 pt-6">
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-purple-900">Choose a deck</h1>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
            {MEMORY_DECKS.map(d => (
              <button key={d.key} data-testid={`deck-${d.key}`} onClick={() => setDeck(d)} className="ll-tile bg-white flex flex-col items-center p-5">
                <div className="text-5xl">{d.emoji}</div><div className="font-display font-bold text-lg text-purple-900 mt-2">{d.label}</div>
              </button>
            ))}
          </div>
          <h2 className="font-display font-bold text-2xl text-purple-900 mt-8">Difficulty</h2>
          <div className="flex gap-3 mt-3">
            {LEVELS.map(l => (
              <button key={l.key} data-testid={`level-${l.key}`} onClick={() => setLevel(l)} className={`ll-btn ${level.key === l.key ? "ll-btn-primary" : "ll-btn-secondary"}`}>{l.emoji} {l.label} · {l.pairs} pairs</button>
            ))}
          </div>
        </main>
      </div>
    </>
  );

  const pct = Math.round((matched.length / cards.length) * 100) || 0;
  return (
    <>
      <TopBar title={`Memory · ${deck.label}`} backTo="/play" />
      <div className="ll-bg min-h-screen pb-32">
        <div className="max-w-3xl mx-auto px-4 pt-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="text-sm font-bold text-purple-600" data-testid="memory-moves">Moves: {moves}</div>
            <div className="flex-1"><ProgressBar percent={pct} /></div>
            <button data-testid="change-deck-button" onClick={() => setDeck(null)} className="ll-btn ll-btn-secondary text-sm py-2">Decks</button>
            <button data-testid="restart-button" onClick={() => start()} className="ll-btn ll-btn-secondary text-sm py-2">Restart</button>
          </div>
          <div className="ll-card p-4 sm:p-6">
            <div className={`grid ${level.cols} gap-3 sm:gap-4`}>
              {cards.map((c, i) => {
                const flipped = open.includes(i) || matched.includes(i);
                return (
                  <button key={c.id} data-testid={`memory-card-${i}`} onClick={() => flip(i)}
                    className={`aspect-square rounded-3xl flex items-center justify-center ${c.text ? "font-display font-bold text-4xl sm:text-5xl text-purple-700" : "text-5xl sm:text-6xl"} transition-transform duration-300 ${flipped ? "bg-white ring-4 ring-purple-300" : "bg-gradient-to-br from-purple-400 to-pink-400 text-white"} shadow-lg`}>
                    {flipped ? <span className="ll-pop">{c.v}</span> : <span className="opacity-70 text-4xl">✦</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {done && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
            <Confetti show />
            <div className="ll-card p-8 text-center max-w-md ll-pop" data-testid="done-card">
              <div className="text-6xl">🏆</div>
              <h2 className="font-display font-bold text-3xl text-purple-900 mt-2">Amazing memory!</h2>
              <p className="text-purple-700 mt-1">Solved in {moves} moves · +{level.bonus} bonus ⭐</p>
              <button onClick={() => start()} data-testid="play-again-button" className="ll-btn ll-btn-primary mt-4">Play again</button>
              <button onClick={() => nav("/play")} data-testid="back-to-play-button" className="ll-btn ll-btn-secondary mt-3 block mx-auto">Back to games</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
